import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Product,
	Severity,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useSitePersonalizationQuery } from '@confluence/onboarding-helpers/entry-points/hooks/useSitePersonalizationQuery.ts';
import { UNSAFE_expValNoExposure } from '@confluence/feature-experiments';

import type { CommerceBackendName } from '../useCommerceBackendName.ts';
import type { BillingSourceSystem } from '../__generated__/useIsPreDunningPeriodTrialDetailsQuery.graphql.ts';

export const useAutoUpgradeNotATrialExperiment = ({
	isSiteAdmin,
	edition,
	hasBillingInformation,
	isInTrialOrPredunning,
	commerceBackendName,
	source,
	skip,
	analyticAttributes = {},
}: {
	isSiteAdmin: boolean;
	edition: ConfluenceEdition | null;
	hasBillingInformation: boolean;
	isInTrialOrPredunning: boolean;
	commerceBackendName?: CommerceBackendName | BillingSourceSystem;
	source?: string;
	skip?: boolean;
	analyticAttributes?: any;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();

	const shouldCheckIfAutoUpgrade =
		isSiteAdmin &&
		edition === ConfluenceEdition.STANDARD &&
		!hasBillingInformation &&
		commerceBackendName === 'CCP' &&
		isInTrialOrPredunning;

	const { loading, error, attributes } = useSitePersonalizationQuery({
		cloudId,
		skip: skip || !shouldCheckIfAutoUpgrade,
	});

	const isAutoUpgrade = !!attributes.find(
		(attribute: { name: string; value: string }) =>
			attribute.name === 'confluence_ccp_auto_upgraded_trialing_and_predunning_sites',
	);

	if (error) {
		const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
			product: Product.CONFLUENCE,
			team: Team.GROWTH_LUNA,
			experimentMeta: {
				experimentKey: 'auto_upgrade_not_a_trial',
			},
			errorType: ErrorType.GRAPHQL,
			errorMessage: error.message,
			source: `useSitePersonalizationQuery hook`,
			severity: Severity.HIGH,
		});

		createAnalyticsEvent(eventPayload).fire();
	}

	let autoUpgradeNotATrialExperimentCohort = 'not-enrolled';

	if (!loading && !error && isAutoUpgrade) {
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
		autoUpgradeNotATrialExperimentCohort = UNSAFE_expValNoExposure(
			'auto_upgrade_not_a_trial',
			'cohort',
			'not-enrolled',
		);

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'exposed',
				actionSubject: 'feature',
				actionSubjectId: 'autoUpgradeNotATrialExperiment',
				attributes: {
					autoUpgradeNotATrialExperimentCohort,
					...analyticAttributes,
				},
				source,
			},
		}).fire();
	}

	return {
		loading,
		error,
		autoUpgradeNotATrialExperimentCohort,
	};
};
